import type { Command, Perspective } from '@integrate/contracts';
import { currentBucket, type SeparationState } from '@integrate/core';

/**
 * The command that takes a head designation away — which is not always the same command.
 *
 * TWO COMMANDS EXIST AND THEY MEAN DIFFERENT THINGS. `clearHead` deletes the designation, which
 * restores inheritance: it says "nobody has decided who heads this group here". `setHead` with a null
 * employee says "somebody decided this group has NO head", and inheritance must not override it.
 * Choosing the wrong one is not cosmetic — on the RemainCo side it resurrects the very person the
 * reader just removed, and the button appears to have done nothing.
 *
 * THE QUESTION THAT DECIDES IT is not "is this designation explicit or inherited". It is: **after
 * removing it, would inheritance put this same person straight back?** Those come apart in a case the
 * panel can reach: a group whose remain head is explicitly X and whose as-is head is also X. The
 * designation is explicit, so an "is it inherited" test says `clearHead` — and `clearHead` would let
 * the as-is row inherit X back again. So the test has to look forward, not backward.
 *
 * AND IT HAS TO INCLUDE THE BUCKET, which the person panel's hand-rolled copy of this did not.
 * `getHeadFor` only inherits an as-is head into RemainCo while that person is still staying
 * (`fields.ts:447`), so if X has moved to the carve-out, inheritance would NOT bring them back and
 * `clearHead` is the honest answer. Without the bucket check the panel wrote an explicit "no head"
 * over a group where nobody had decided anything — a small lie, but one that then blocks a later
 * as-is designation from ever reaching RemainCo.
 *
 * ONE FUNCTION BECAUSE THERE ARE NOW TWO CONTROLS. The person's own panel toggles their star, and the
 * group's panel removes whoever is named on it. Two copies of a rule this subtle would drift, and the
 * first copy had already drifted from the rule it was written against.
 */
export function removeHeadCommand(
  state: SeparationState,
  {
    scenarioId,
    perspective,
    groupId,
    headId,
  }: { scenarioId: string; perspective: Perspective; groupId: string; headId: string },
): Command {
  const asIsHead = state.design.heads['as-is']?.[groupId];
  const inheritanceWouldRestore =
    perspective === 'remain' &&
    asIsHead === headId &&
    currentBucket(state.design, headId) === 'Remain';

  return inheritanceWouldRestore
    ? { type: 'setHead', scenarioId, perspective, groupId, employeeId: null }
    : { type: 'clearHead', scenarioId, perspective, groupId };
}
